import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import { subscribeToTier } from '../../Tier/redux/consumer/actions';
import { isConsumerReduxReady, isMatureFilterOverridden } from '../../Tier/redux/consumer/selectors';
import { useDeviationMatureFilter } from '../../utils/hooks/useDeviationMatureFilter';
import { DeviationResourceType } from '../../types/deviation';
import { useRestrictToLoggedIn } from '../../utils/hooks/useRestrictToLoggedIn';
import { formatPrice } from '../../DeviationViews/TierProductCardDumb/utils';
export function useSubscribeButton(tier, { type , matureFilterDisabled , referral_info , showUnsubAsView , withPrice , href: hrefProp  } = {}) {
    const waitingAction = useRef();
    const [loadRedux, setLoadRedux] = useState(false);
    const { t  } = useTranslation();
    const { showBlocked  } = useDeviationMatureFilter(tier);
    const reduxReady = useSelector(isConsumerReduxReady);
    const matureFilterOverridden = useSelector((state)=>{
        return isMatureFilterOverridden(state, tier === null || tier === void 0 ? void 0 : tier.deviationId);
    });
    const dispatch = useDispatch();
    const { restrictToLoggedIn  } = useRestrictToLoggedIn();
    const isMatureBlocked = showBlocked && !matureFilterDisabled && !matureFilterOverridden;
    useEffect(()=>{
        if (reduxReady && waitingAction.current) {
            waitingAction.current();
            waitingAction.current = undefined;
        }
    }, [
        reduxReady
    ]);
    useEffect(()=>{
        if (isMatureBlocked) {
            // mature blocked shows disabled, so we cannot wait for a click
            // to load the redux stuff
            setLoadRedux(true);
        }
    }, [
        isMatureBlocked
    ]);
    const variant = useMemo(()=>{
        var _tier_tier, _tier_tier1;
        if (!tier) {
            return 'disabled';
        }
        if (type) {
            return type;
        }
        if (tier === null || tier === void 0 ? void 0 : (_tier_tier = tier.tier) === null || _tier_tier === void 0 ? void 0 : _tier_tier.isUserSubscribed) {
            if (showUnsubAsView) {
                return 'view';
            }
            return 'subscribed';
        }
        if (isMatureBlocked || !(tier === null || tier === void 0 ? void 0 : (_tier_tier1 = tier.tier) === null || _tier_tier1 === void 0 ? void 0 : _tier_tier1.canUserSubscribe)) {
            return 'disabled';
        }
        return 'subscribe';
    }, [
        isMatureBlocked,
        showUnsubAsView,
        tier,
        type
    ]);
    const subscribe = useCallback((e)=>{
        if (!tier) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        if (!reduxReady) {
            waitingAction.current = ()=>{
                dispatch(subscribeToTier(tier));
            };
            setLoadRedux(true);
            return;
        }
        dispatch(subscribeToTier(tier));
    }, [
        dispatch,
        reduxReady,
        tier
    ]);
    const { onClick , href , biData , text , disabled  } = useMemo(()=>{
        switch(variant){
            case 'subscribe':
                var _tier_tier, _tier_tier1;
                return {
                    onClick: restrictToLoggedIn(ReferralType.MEMBER_SUBSCRIBE, subscribe),
                    href: hrefProp,
                    biData: BiData({
                        evid: 550,
                        typeid: DeviationResourceType.TIER,
                        itemid: tier.deviationId,
                        referral_info: referral_info !== null && referral_info !== void 0 ? referral_info : BiContextProvidedValue
                    }),
                    text: withPrice && (tier === null || tier === void 0 ? void 0 : (_tier_tier = tier.tier) === null || _tier_tier === void 0 ? void 0 : _tier_tier.dollarPrice) ? t('deviation.tier.subscribeWithPrice', {
                        dollarPrice: formatPrice(tier === null || tier === void 0 ? void 0 : (_tier_tier1 = tier.tier) === null || _tier_tier1 === void 0 ? void 0 : _tier_tier1.dollarPrice)
                    }) : t('deviation.tier.subscribe')
                };
            case 'view':
                return {
                    href: Url.userSubscriptionsPath({
                        username: tier.author.username,
                        tierDeviationId: tier === null || tier === void 0 ? void 0 : tier.deviationId,
                        tierContentType: 'deviations'
                    }),
                    biData: BiData({
                        evid: 544,
                        typeid: DeviationResourceType.TIER,
                        itemid: tier.deviationId
                    }),
                    text: t('deviation.tier.viewSubscription')
                };
            case 'subscribed':
                return {
                    href: hrefProp,
                    biData: BiData({
                        evid: 551,
                        typeid: DeviationResourceType.TIER,
                        itemid: tier.deviationId,
                        referral_info: (typeof window !== 'undefined' && window.location.href === URLS.ORDERS ? ReferralType.BILLING_PAYMENTS : ReferralType.TIER_CARD) || ReferralType.TIER_CARD
                    }),
                    text: t('deviation.tier.subscribed')
                };
            case 'disabled':
                return {
                    text: t('deviation.tier.noSubscribing'),
                    disabled: true
                };
            default:
                return {};
        }
    }, [
        hrefProp,
        referral_info,
        restrictToLoggedIn,
        subscribe,
        t,
        tier,
        variant,
        withPrice
    ]);
    return {
        loadRedux,
        variant,
        onClick,
        href,
        biData,
        text,
        disabled
    };
}
